import React, { useState } from 'react';

const scoreColor = s => s >= 4?'#2E7D32':s >= 3?'#1565C0':s >= 2?'#F57F17':'#C62828';

export default function DocumentViewer({ doc, onClose }) {
  const [activeTab, setActiveTab] = useState('viewer');
  const analysis = doc.analysis;
  const results = analysis?.results || [];

  const parsePage = (citation) => {
    if (!citation) return null;
    const m = citation.match(/Page\s+(\d+)/i);
    return m ? parseInt(m[1]) : null;
  };

  const pdfUrl = doc.preview_url;
  const [jumpPage, setJumpPage] = useState(null);

  const jumpToCitation = (citation) => {
    const page = parsePage(citation);
    if (page && pdfUrl) {
      setJumpPage(page);
      setActiveTab('viewer');
    }
  };

  const iframeSrc = pdfUrl
    ? (jumpPage ? `${pdfUrl}#page=${jumpPage}` : pdfUrl)
    : null;

  return (
    <div style={{
      position:'fixed',top:0,left:0,right:0,bottom:0,
      background:'rgba(0,0,0,0.6)',zIndex:1000,
      display:'flex',justifyContent:'center',alignItems:'center'
    }} onClick={onClose}>
      <div style={{
        background:'#fff',borderRadius:12,width:'90vw',height:'90vh',
        display:'flex',flexDirection:'column',overflow:'hidden'
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          padding:'12px 16px',borderBottom:'1px solid #E8ECF0',
          display:'flex',alignItems:'center',gap:12
        }}>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600,color:'#0D1B2A'}}>{doc.file_name}</div>
            <div style={{fontSize:10,color:'#7A8899'}}>
              {(doc.size_bytes / 1024).toFixed(0)} KB
              {analysis && <span> &middot; {results.length} controls scored</span>}
            </div>
          </div>
          <div style={{display:'flex',gap:4}}>
            <button
              onClick={() => setActiveTab('viewer')}
              style={{
                fontSize:10,padding:'4px 12px',borderRadius:4,cursor:'pointer',
                background: activeTab === 'viewer' ? '#1565C0' : '#F5F5F5',
                color: activeTab === 'viewer' ? '#fff' : '#455A64',
                border:'none'
              }}>Document</button>
            <button
              onClick={() => setActiveTab('findings')}
              style={{
                fontSize:10,padding:'4px 12px',borderRadius:4,cursor:'pointer',
                background: activeTab === 'findings' ? '#1565C0' : '#F5F5F5',
                color: activeTab === 'findings' ? '#fff' : '#455A64',
                border:'none'
              }}>AI Findings ({results.length})</button>
          </div>
          <button onClick={onClose} style={{
            background:'none',border:'none',fontSize:18,cursor:'pointer',
            color:'#7A8899',padding:'4px 8px'
          }}>X</button>
        </div>

        {/* Body */}
        <div style={{flex:1,overflow:'hidden',display:'flex'}}>
          {activeTab === 'viewer' && (
            <div style={{flex:1,display:'flex',flexDirection:'column'}}>
              {iframeSrc ? (
                <iframe
                  key={jumpPage || 'default'}
                  src={iframeSrc}
                  style={{flex:1,border:'none',width:'100%'}}
                  title="Document preview"
                />
              ) : (
                <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'#7A8899'}}>
                  <div style={{textAlign:'center'}}>
                    <div style={{fontSize:32,marginBottom:8}}>PDF</div>
                    <div style={{fontSize:12}}>Preview not available</div>
                    <div style={{fontSize:10,marginTop:4}}>Document may be stored in-memory or SAS URL generation is not configured</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'findings' && (
            <div style={{flex:1,overflow:'auto',padding:16}}>
              {results.length === 0 ? (
                <div style={{textAlign:'center',padding:40,color:'#7A8899',fontSize:12}}>
                  No AI analysis results available yet.
                </div>
              ) : (
                results.map(r => (
                  <div key={r.control_id} style={{
                    marginBottom:16,border:'1px solid #E8ECF0',borderRadius:8,overflow:'hidden'
                  }}>
                    {/* Result header */}
                    <div style={{
                      padding:'10px 14px',background:'#F8F9FA',
                      display:'flex',alignItems:'center',gap:10,
                      borderBottom:'1px solid #E8ECF0'
                    }}>
                      <span style={{fontWeight:600,fontSize:11,color:'#1565C0'}}>{r.control_id}</span>
                      <span style={{
                        fontSize:10,fontWeight:600,color:'#fff',
                        background:scoreColor(r.score),
                        padding:'2px 8px',borderRadius:10
                      }}>Score: {r.score}</span>
                      <span className="pill" style={{
                        fontSize:9,
                        background: r.status === 'provided' ? '#E8F5E9' : r.status === 'partial' ? '#FFF3E0' : '#FFEBEE',
                        color: r.status === 'provided' ? '#2E7D32' : r.status === 'partial' ? '#E65100' : '#C62828'
                      }}>{r.status}</span>
                      <span style={{fontSize:9,color:'#7A8899',marginLeft:'auto'}}>
                        Confidence: {r.confidence}
                      </span>
                    </div>

                    {/* Rationale */}
                    <div style={{padding:'10px 14px',fontSize:11,color:'#455A64',lineHeight:1.5}}>
                      {r.rationale}
                    </div>

                    {/* Findings checklist */}
                    {r.findings && r.findings.length > 0 && (
                      <div style={{padding:'0 14px 10px'}}>
                        <div style={{fontSize:10,fontWeight:600,color:'#0D1B2A',marginBottom:6}}>Requirements</div>
                        {r.findings.map((f, fi) => (
                          <div key={fi} style={{
                            display:'flex',gap:8,alignItems:'flex-start',marginBottom:6,
                            fontSize:10,lineHeight:1.4
                          }}>
                            <span style={{
                              color: f.met ? '#2E7D32' : '#C62828',
                              fontWeight:700,fontSize:12,flexShrink:0
                            }}>{f.met ? '✓' : '✗'}</span>
                            <div>
                              <div style={{color:'#455A64'}}>{f.requirement}</div>
                              {f.citation && (
                                <div
                                  onClick={() => jumpToCitation(f.citation)}
                                  style={{
                                    fontSize:9,color:'#1565C0',marginTop:2,
                                    cursor: parsePage(f.citation) && pdfUrl ? 'pointer' : 'default',
                                    textDecoration: parsePage(f.citation) && pdfUrl ? 'underline' : 'none'
                                  }}
                                  title={parsePage(f.citation) ? `Jump to page ${parsePage(f.citation)}` : ''}>
                                  {f.citation}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Gaps */}
                    {r.gaps && r.gaps.length > 0 && (
                      <div style={{padding:'0 14px 10px'}}>
                        <div style={{fontSize:10,fontWeight:600,color:'#C62828',marginBottom:4}}>Gaps</div>
                        {r.gaps.map((g, gi) => (
                          <div key={gi} style={{fontSize:10,color:'#C62828',marginBottom:2}}>
                            - {g}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
